import { Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, Sparkles, Users, CreditCard,
  ShieldCheck, LogOut, Handshake, BadgeCheck, FlaskConical, Lock, BookOpen, BarChart3, Siren, Target, Radar, ClipboardCheck,
} from "lucide-react";import { Logo } from "@/components/AppShell";
import {
  isStaff, canSeeAdminCenter, canSeeSecurityDashboard, canSeeSystemIntelligence,
  canSeeIncidentResponse, canSeeLeadGeneration, canSeeBetaReport, canSeePreviewTools,
  canSeeReviewerWorkload, canSeeDemandGaps, roleLabel,
} from "@/lib/roles";

const CUSTOMER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/onboarding", label: "Verification", icon: BadgeCheck, testid: "nav-onboarding" },
  { to: "/profile", label: "Business Profile", icon: Building2, testid: "nav-profile" },
  { to: "/matches", label: "AI Matches", icon: Sparkles, testid: "nav-matches" },
  { to: "/needs", label: "Needs & Opportunities", icon: Target, testid: "nav-needs" },
  { to: "/connections", label: "Connections", icon: Handshake, testid: "nav-connections" },
  { to: "/directory", label: "Directory", icon: Users, testid: "nav-directory" },
  { to: "/deals", label: "Deal Room", icon: Handshake, testid: "nav-deals" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/security", label: "Security", icon: Lock, testid: "nav-security" },
];

// Roles that do operational/deal work also get Directory + Deal Room.
const OPS_ROLES = ["admin", "compliance_director", "super_admin"];

// Tailored base for internal staff. Customer-account tools (Business Profile,
// AI Matches, Billing, personal Verification) are hidden — those are for running
// a member business, not for employee accounts.
function baseNavFor(role) {
  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  ];
  if (OPS_ROLES.includes(role)) {
    nav.push({ to: "/directory", label: "Directory", icon: Users, testid: "nav-directory" });
    nav.push({ to: "/deals", label: "Deal Room", icon: Handshake, testid: "nav-deals" });
  }
  nav.push({ to: "/security", label: "Security", icon: Lock, testid: "nav-security" });
  return nav;
}

// The demand → recruitment pipeline, surfaced as ONE clearly-ordered group for founder/senior staff:
// Needs & Opportunities → Demand Gaps → Scout & Recruitment. Reuses the EXISTING /needs, /demand-gaps and
// /lead-generation routes (no duplicate pages); RBAC mirrors the backend route guards.
function demandNavFor(role) {
  const d = [];
  if (canSeeDemandGaps(role)) {
    d.push({ to: "/needs", label: "Needs & Opportunities", icon: Target, testid: "nav-needs", tone: "blue" });
    d.push({ to: "/demand-gaps", label: "Demand Gaps", icon: Radar, testid: "nav-demand-gaps", tone: "blue" });
  }
  if (canSeeLeadGeneration(role))
    d.push({ to: "/lead-generation", label: "Scout & Recruitment", icon: BookOpen, testid: "nav-lead-generation", tone: "green" });
  if (canSeeLeadGeneration(role))
    d.push({ to: "/cohort-readiness", label: "Cohort Readiness", icon: ClipboardCheck, testid: "nav-cohort-readiness", tone: "green" });
  return d;
}

// Role-scoped staff consoles (mirror backend route guards; backend stays final authority).
function consolesFor(role) {
  const c = [];
  if (canSeeAdminCenter(role))
    c.push({ to: "/admin", label: "Admin Control Center", icon: ShieldCheck, testid: "nav-admin", tone: "orange" });
  if (canSeeSecurityDashboard(role))
    c.push({ to: "/security-admin", label: "Security Dashboard", icon: ShieldCheck, testid: "nav-security-admin", tone: "orange" });
  if (canSeeSystemIntelligence(role))
    c.push({ to: "/system-intelligence", label: "System Intelligence", icon: BookOpen, testid: "nav-system-intelligence", tone: "blue" });
  if (canSeeIncidentResponse(role))
    c.push({ to: "/incident-response", label: "Incident Response", icon: Siren, testid: "nav-incident-response", tone: "red" });
  if (canSeeReviewerWorkload(role))
    c.push({ to: "/reviewer-workload", label: "Reviewer Workload", icon: ClipboardCheck, testid: "nav-reviewer-workload", tone: "blue" });
  if (canSeeBetaReport(role))
    c.push({ to: "/beta-report", label: "Beta Report", icon: BarChart3, testid: "nav-beta-report", tone: "green" });
  if (canSeePreviewTools(role))
    c.push({ to: "/preview-tools", label: "Preview Tools", icon: FlaskConical, testid: "nav-preview-tools", tone: "orange" });
  return c;
}

const TONE = {
  orange: { active: "bg-[#C2410C] text-white", idle: "text-[#C2410C] hover:bg-orange-50" },
  blue: { active: "bg-[#1D4ED8] text-white", idle: "text-[#1D4ED8] hover:bg-blue-50" },
  green: { active: "bg-[#047857] text-white", idle: "text-[#047857] hover:bg-emerald-50" },
  red: { active: "bg-[#B91C1C] text-white", idle: "text-[#B91C1C] hover:bg-red-50" },
};

function NavItem({ item, active, onNavigate }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={item.testid}
      onClick={onNavigate}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
        active ? "bg-[#0A0F1C] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#0A0F1C]"
      }`}
    >
      <Icon size={18} /> {item.label}
    </Link>
  );
}

function ConsoleItem({ item, active, onNavigate }) {
  const Icon = item.icon;
  const tone = TONE[item.tone] || TONE.orange;
  return (
    <Link
      to={item.to} data-testid={item.testid} onClick={onNavigate}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${active ? tone.active : tone.idle}`}
    >
      <Icon size={18} /> {item.label}
    </Link>
  );
}

function UserCard({ user }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="h-9 w-9 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center shrink-0">
        {user?.picture
          ? <img src={user.picture} alt="" className="h-full w-full object-cover" />
          : <span className="font-heading font-bold text-slate-600 text-sm">{user?.name?.[0]?.toUpperCase() || "U"}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-[#0A0F1C] truncate">{user?.name}</div>
        <div className="text-xs text-slate-400 truncate">{user?.email}</div>
      </div>
    </div>
  );
}

export default function Sidebar({ user, pathname, onNavigate, onLogout }) {
  const role = user?.role;
  const staff = isStaff(role);
  const baseNav = staff ? baseNavFor(role) : CUSTOMER_NAV;
  const demandNav = staff ? demandNavFor(role) : [];
  const consoles = staff ? consolesFor(role) : [];

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-200">
        <Logo />
        {staff && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600" data-testid="workspace-role-badge">
            <ShieldCheck size={11} /> {roleLabel(role)}
          </div>
        )}
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {baseNav.map((item) => (
          <NavItem key={item.to} item={item} active={pathname === item.to} onNavigate={onNavigate} />
        ))}
        {demandNav.length > 0 && (
          <div className="pt-3 mt-2 border-t border-slate-200" data-testid="demand-pipeline-group">
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400" data-testid="demand-pipeline-heading">Demand &amp; Recruitment</div>
            {demandNav.map((item, i) => (
              <div key={item.to}>
                <ConsoleItem item={item} active={pathname === item.to} onNavigate={onNavigate} />
                {i < demandNav.length - 1 && (
                  <div className="pl-6 text-slate-300 leading-none" aria-hidden="true">↓</div>
                )}
              </div>
            ))}
          </div>
        )}
        {consoles.length > 0 && (
          <div className="pt-3 mt-2 border-t border-slate-200">
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400" data-testid="staff-consoles-heading">Staff workspace</div>
            {consoles.map((item) => (
              <ConsoleItem key={item.to} item={item} active={pathname === item.to} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </nav>
      <div className="px-3 py-4 border-t border-slate-200">
        <UserCard user={user} />
        <button
          onClick={onLogout} data-testid="logout-button"
          className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut size={18} /> Log out
        </button>
      </div>
    </div>
  );
}
